/**
 * Add a task, from wherever you happen to be.
 *
 * Opened from a subcategory tile it arrives with that category and
 * subcategory already chosen, because you have already said where the work
 * goes by clicking there. Opened from "Everything open" it arrives blank,
 * because you have not.
 *
 * A modal rather than a permanently visible form: a form at the top of the
 * page occupies the space every time you look at the dashboard and is used a
 * fraction of those times.
 *
 * It is centred on every screen size, including a phone.
 */

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'

import type { Category, ItemCreatePayload, ItemPriority } from '@/api/types'
import { PRIORITIES } from '@/api/types'
import { PRIORITY_LABEL } from '@/lib/format'

interface AddTaskModalProps {
  open: boolean
  categories: Category[]
  /** Preselected when opened from inside a subcategory tile. */
  defaultSubcategoryId?: number | null
  /** Preselected when opened from a priority tile, which says how urgent the
   *  task is but nothing about where it goes. */
  defaultPriority?: ItemPriority | null
  onClose: () => void
  onCreate: (payload: ItemCreatePayload) => Promise<void>
}

export function AddTaskModal({
  open,
  categories,
  defaultSubcategoryId,
  defaultPriority,
  onClose,
  onCreate,
}: AddTaskModalProps) {
  const [categoryId, setCategoryId] = useState<number | ''>('')
  const [subcategoryId, setSubcategoryId] = useState<number | ''>('')
  const [title, setTitle] = useState('')
  const [priority, setPriority] = useState<ItemPriority>('medium')
  const [dueDate, setDueDate] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const titleRef = useRef<HTMLInputElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  // Reset every time it opens, so yesterday's half-typed task never reappears.
  useEffect(() => {
    if (!open) return
    const owner = defaultSubcategoryId
      ? categories.find((c) => c.subcategories.some((s) => s.id === defaultSubcategoryId))
      : undefined
    setCategoryId(owner?.id ?? '')
    setSubcategoryId(defaultSubcategoryId ?? '')
    setTitle('')
    setPriority(defaultPriority ?? 'medium')
    setDueDate('')
    setError(null)
    // A frame's delay: the input does not exist until this render commits.
    const timer = setTimeout(() => titleRef.current?.focus(), 30)
    return () => clearTimeout(timer)
  }, [open, defaultSubcategoryId, defaultPriority, categories])

  // Escape closes, and focus is kept inside while it is open.
  useEffect(() => {
    if (!open) return
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return
      const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
        'input, select, button, [href], textarea',
      )
      if (focusable.length === 0) return
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const subcategories = useMemo(() => {
    if (categoryId === '') return []
    return categories.find((c) => c.id === categoryId)?.subcategories ?? []
  }, [categories, categoryId])

  if (!open) return null

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (busy) return
    if (subcategoryId === '') {
      setError('Choose where this goes.')
      return
    }
    if (!title.trim()) {
      setError('Give the task a name.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      await onCreate({
        subcategory_id: subcategoryId,
        title: title.trim(),
        priority,
        due_date: dueDate || null,
      })
      onClose()
    } catch {
      // onCreate surfaces its own message in a toast; keep the modal open so
      // nothing typed is lost.
      setError('Could not add that task.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      // Clicking the backdrop closes; clicking inside must not.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-task-title"
        // Centred at every size. It used to rise from the bottom edge on a
        // phone, which put the fields under the thumb but also under the
        // on-screen keyboard. max-h with its own scroll keeps the buttons
        // reachable when that keyboard is up on a short screen.
        className="card max-h-[90dvh] w-full max-w-lg overflow-y-auto p-4 shadow-xl"
      >
        <h2 id="add-task-title" className="text-lg font-bold">
          Add a task
        </h2>

        <form onSubmit={submit} className="mt-3 flex flex-col gap-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-ink-2">
              Category
              <select
                className="field mt-1"
                value={categoryId}
                onChange={(event) => {
                  const next = event.target.value ? Number(event.target.value) : ''
                  setCategoryId(next)
                  // The old subcategory belongs to the old category; keeping it
                  // would file the task somewhere the dropdown is not showing.
                  setSubcategoryId('')
                }}
              >
                <option value="">Choose a category...</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-xs font-semibold text-ink-2">
              Subcategory
              <select
                className="field mt-1"
                value={subcategoryId}
                disabled={categoryId === ''}
                onChange={(event) =>
                  setSubcategoryId(event.target.value ? Number(event.target.value) : '')
                }
              >
                <option value="">
                  {categoryId === '' ? 'Pick a category first' : 'Choose a subcategory...'}
                </option>
                {subcategories.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <label className="text-xs font-semibold text-ink-2">
            Task
            <input
              ref={titleRef}
              className="field mt-1"
              placeholder="What needs doing?"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              maxLength={300}
            />
          </label>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-ink-2">
              Priority
              <select
                className="field mt-1"
                value={priority}
                onChange={(event) => setPriority(event.target.value as ItemPriority)}
              >
                {PRIORITIES.map((value) => (
                  <option key={value} value={value}>
                    {PRIORITY_LABEL[value]}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-xs font-semibold text-ink-2">
              Due date <span className="font-normal text-ink-3">(optional)</span>
              <input
                type="date"
                className="field mt-1"
                value={dueDate}
                onChange={(event) => setDueDate(event.target.value)}
              />
            </label>
          </div>

          {error && (
            <p role="alert" className="rounded-md bg-error-light px-3 py-2 text-sm text-error-text">
              {error}
            </p>
          )}

          <div className="mt-1 flex justify-end gap-2">
            <button type="button" className="btn btn-neutral" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Adding...' : 'Add task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
